/**
 * RETAIL PRODUCT FINDER MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 *
 * productCatalog :: the vocabulary the public product filter is allowed to use.
 *
 * WHY THIS EXISTS (backlog items 100 / 101). `retail_locations.products` is a
 * plain `text[]` with three writers and no shared vocabulary:
 *   • the location editor took whatever a human typed;
 *   • the Google Sheets sync took whatever the spreadsheet cell said;
 *   • `resolve_depletion_outlets` derives it from the SKUs actually depleted.
 * Those strings are rendered straight to shoppers as the widget's product
 * filter, so "Chardonnay", "chardonnay", "CHARD" and "Chardonnay 750ml" become
 * four entries in a filter that should have one.
 *
 * The vocabulary is FLAVOR-LEVEL, not SKU-level. A shopper looks for
 * "Chardonnay"; a distributor orders "Chardonnay / 750ml / 6pk". Pointing the
 * finder at `products` would give an 18-entry filter full of case
 * configurations. So a SKU name that comes in is folded DOWN to its flavor —
 * which is also what reconciles the depletions resolver (it writes SKU names)
 * with the other two writers.
 *
 * Matching is deliberately tolerant but never creative: case, punctuation and
 * whitespace are ignored, and trailing size/pack tokens drawn from the tenant's
 * OWN reference data are stripped. Nothing is guessed by edit distance — a fuzzy
 * match that silently renames a real product is worse than an honest miss.
 *
 * An unmatched name is NOT dropped. The store is real, its address is real, and
 * a one-off stockist listing must not be blocked by a vocabulary gap. It is kept
 * verbatim and REPORTED, so the drift is visible instead of silent.
 *
 * SCOPE — what this file does, and what it deliberately does not.
 *
 * It resolves ONE name at a time, for the editor UI: `LocationEditorModal` folds
 * each name as the user adds it, and `LocationsTab` flags stored names that have
 * since fallen off the catalog. That is the whole job.
 *
 * BULK canonicalisation — split a spreadsheet cell, fold every name, dedupe, cap
 * the result, and report what missed — is NOT here, and must not be added here.
 * It has exactly ONE implementation: `canonicalizeProducts()` in
 * `supabase/functions/sheets-sync/index.ts`, which owns the cap order and the
 * off-catalog report. Change bulk behaviour there, and deploy the sync.
 *
 * A second copy of that function lived in this file until APP-673 with zero
 * callers and no tests, under a comment instructing the reader to keep the two
 * in step. Nothing executed it, so nothing noticed when they drifted over where
 * MAX_PRODUCTS was applied (APP-664) — an unexecuted copy does not verify the
 * real one, it just makes a reviewer believe the behaviour was checked twice.
 *
 * `retail_locations.products` has three writers — the editor (this module),
 * `sheets-sync`, and `resolve_depletion_outlets` (migration 0090). What they
 * genuinely share is this vocabulary: flavor-level, matched never guessed, and
 * the two caps below.
 */

import { listFlavors, listProducts, listSizes, listPackSizes } from "../../../shared/utils/referenceDataService.js";

/** Cap on how many product names one location may advertise. It counts DISTINCT
 *  products: the editor folds and dedupes each name as it is added, so the 50
 *  slots are never spent on near-duplicates of one wine. `retailLocationService`
 *  re-applies it on write as a backstop against a hand-built array. */
export const MAX_PRODUCTS = 50;
/** Cap on one product name. Matches referenceDataService's 120-char name limit. */
export const MAX_PRODUCT_LEN = 120;

/**
 * Comparison key: lower case, apostrophes dropped, every other run of
 * non-alphanumerics collapsed to one space. "Chardonnay 750ML" and
 * "chardonnay-750ml" therefore compare equal; "Chardonnay" and "Chard" do not.
 */
export function normalizeProductKey(value) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/['’ʼ`]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

// A bare measurement written as one token — "750ml", "12oz", "6pk". Stripped
// even when the tenant hasn't spelled that size out in System Settings, because
// a measurement is never the thing a shopper is looking for.
const UNIT_TOKEN = /^\d+(?:\.\d+)?(?:ml|l|cl|oz|g|kg|lb|lbs|pk|pack|ct|count|btl|bottle|bottles|can|cans|case|cases)$/;

/**
 * Build the lookup used by matchProductName().
 *
 * @param {object} src
 * @param {Array}  src.flavors    product_flavors rows  [{ id, name }]
 * @param {Array}  src.products   products rows         [{ name, flavor_id, flavor:{name} }]
 * @param {Array}  src.sizes      product_sizes rows    [{ name, abbreviation }]
 * @param {Array}  src.packSizes  pack_sizes rows       [{ name }]
 * @returns {{ canonicalByKey: Map<string,string>, modifiers: Set<string>, names: string[] }}
 */
export function buildProductVocabulary({ flavors = [], products = [], sizes = [], packSizes = [] } = {}) {
  const canonicalByKey = new Map();
  const names = [];

  // First writer wins: the flavor list is loaded first, so a SKU whose name
  // happens to equal a flavor name can never redirect that flavor elsewhere.
  const add = (raw, canonical) => {
    const k = normalizeProductKey(raw);
    if (!k || !canonical) return;
    if (!canonicalByKey.has(k)) canonicalByKey.set(k, canonical);
  };

  const flavorNameById = new Map();
  for (const f of flavors) {
    if (!f?.name) continue;
    flavorNameById.set(f.id, f.name);
    add(f.name, f.name);
    names.push(f.name);
  }

  for (const p of products) {
    if (!p?.name) continue;
    // Fold the SKU down to its flavor. A SKU with no flavor attached (rare, but
    // the column is nullable) canonicalises to itself rather than to nothing.
    const canonical = p.flavor?.name || flavorNameById.get(p.flavor_id) || p.name;
    add(p.name, canonical);
  }

  const modifiers = new Set();
  const addModifier = (raw) => {
    const k = normalizeProductKey(raw);
    if (k) modifiers.add(k);
  };
  for (const s of sizes) { addModifier(s?.name); addModifier(s?.abbreviation); }
  for (const p of packSizes) addModifier(p?.name);

  return {
    canonicalByKey,
    modifiers,
    names: [...new Set(names)].sort((a, b) => a.localeCompare(b)),
  };
}

/** Strip trailing size / pack / measurement tokens off a normalized key. */
function stripTrailingModifiers(key, modifiers) {
  let tokens = key.split(" ").filter(Boolean);
  let changed = true;
  while (changed && tokens.length > 1) {
    changed = false;
    // Multi-word modifiers ("half bottle") as well as single tokens.
    for (let take = Math.min(3, tokens.length - 1); take >= 1; take--) {
      const tail = tokens.slice(tokens.length - take).join(" ");
      if (modifiers.has(tail) || (take === 1 && UNIT_TOKEN.test(tail))) {
        tokens = tokens.slice(0, tokens.length - take);
        changed = true;
        break;
      }
    }
  }
  return tokens.join(" ");
}

/**
 * Resolve one incoming name against the tenant's catalog.
 * @returns {{ canonical: string, matched: boolean }} — `canonical` is the name to
 *          store: the catalog's spelling on a hit, the cleaned input on a miss.
 */
export function matchProductName(raw, vocab) {
  const trimmed = String(raw ?? "").trim().replace(/\s+/g, " ").slice(0, MAX_PRODUCT_LEN);
  if (!trimmed) return { canonical: "", matched: false };
  const { canonicalByKey, modifiers } = vocab || {};
  if (!canonicalByKey || canonicalByKey.size === 0) return { canonical: trimmed, matched: false };

  const attempt = (candidate) => {
    const key = normalizeProductKey(candidate);
    if (!key) return null;
    const direct = canonicalByKey.get(key);
    if (direct) return direct;
    const stripped = stripTrailingModifiers(key, modifiers || new Set());
    if (stripped && stripped !== key) return canonicalByKey.get(stripped) || null;
    return null;
  };

  const hit = attempt(trimmed);
  if (hit) return { canonical: hit, matched: true };

  // "Flavor / Size / Pack" — the exact shape our own starter sheet used to teach
  // (backlog item 102). Take the leading segment and try again.
  if (trimmed.includes("/")) {
    const lead = trimmed.split("/")[0];
    const segHit = attempt(lead);
    if (segHit) return { canonical: segHit, matched: true };
  }

  return { canonical: trimmed, matched: false };
}

/** True when this stored name is not in the tenant's catalog. */
export function isOffCatalog(name, vocab) {
  if (!vocab?.canonicalByKey?.size) return false;
  return !matchProductName(name, vocab).matched;
}

/** Names on a location that aren't in the catalog. */
export function offCatalogProducts(products, vocab) {
  return (products || []).filter((p) => isOffCatalog(p, vocab));
}

/**
 * Load the tenant's vocabulary from SHARED reference data.
 *
 * `products` / `product_flavors` / `product_sizes` / `pack_sizes` are
 * cross-module reference data owned by System Settings and read through
 * `/shared/utils/referenceDataService.js` — not a cross-module import. All four
 * reads are RLS-scoped to the caller's workspace.
 *
 * NEVER THROWS. The store list must render for a tenant that has no catalog yet
 * (or whose plan doesn't include System Settings); an empty vocabulary simply
 * means nothing can be judged off-catalog, which is the honest answer.
 */
export async function loadProductVocabulary() {
  try {
    const [flavors, products, sizes, packSizes] = await Promise.all([
      listFlavors(), listProducts(), listSizes(), listPackSizes(),
    ]);
    return buildProductVocabulary({ flavors, products, sizes, packSizes });
  } catch (e) {
    console.error("[productCatalog.loadProductVocabulary]", e);
    return buildProductVocabulary({});
  }
}
